import React, { useEffect, useMemo, useState } from 'react';
import { Box, Dialog } from '@mui/material';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import { ColorPalette } from '../../styles/constants';
import {
  ProjectAllocations,
  ReportFilters,
  ViewMode,
} from '../../models/reports';
import { getProjectsAllocations } from '../../services/api/reportsServices';
import AppLoading from '../app/appLoading';
import useAppSnackbar from '../../hooks/useAppSnackbar';
import { ReportsInputsData } from './reportsInputsData';
import { ProjectAllocationsCard } from './projectAllocationsCard';
import { ROLES_ID } from '../../models/employee';
import { useReportsViewMode } from '../../context/reports/useReportsCtx';

const styles = {
  dialog: {
    maxWidth: '800px',
    overflowX: 'hidden',
    position: 'relative',
  },
  topBox: {
    p: 3,
    background: ColorPalette.BACKGROUND,
  },
  inputDataBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    mb: 2,
  },
  inputValue: {
    color: ColorPalette.RAISIN_BLACK,
  },
  allocationsListBox: {
    maxHeight: '400px',
  },
};

interface AllocationsModalProps {
  isOpen: boolean;
  viewUnloggedProjects: boolean;
  handleClose: () => void;
  filters: ReportFilters;
}

export function ProjectAllocationsModal({
  isOpen,
  handleClose,
  viewUnloggedProjects,
  filters,
}: AllocationsModalProps): JSX.Element {
  const [viewMode] = useReportsViewMode();
  const { onErrorSnackbar } = useAppSnackbar();

  const { managerId, employeeId, startDate, endDate, projectId, towerId } = filters;

  const [projectsAllocations, setProjectsAllocations] = useState<
    ProjectAllocations[]
  >([]);

  const [isLoading, setIsLoading] = useState(true);

  const roleId = useMemo(() => {
    switch (viewMode) {
      case ViewMode.PROJECT:
        return ROLES_ID.PROJECT_MANAGER;

      default:
        return ROLES_ID.PROJECT_MANAGER;
    }
  }, [viewMode]);

  useEffect(() => {
    if (isOpen) {
      const filteringPayload = {
        startDate,
        endDate,
        employeeId,
        projectId,
        managerId: managerId || null,
        userRoleId: roleId,
        towerId: towerId || null,
      };
      
      getProjectsAllocations(filteringPayload, viewUnloggedProjects)
        .then((resp) => {
          setProjectsAllocations(resp.items);
        })
        .catch(onErrorSnackbar)
        .finally(() => setIsLoading(false));
    }
  }, [
    viewUnloggedProjects,
    employeeId,
    endDate,
    isOpen,
    managerId,
    onErrorSnackbar,
    projectId,
    roleId,
    startDate,
    towerId,
  ]);

  return (
    <Dialog open={isOpen} PaperProps={{ sx: styles.dialog }}>
      <AppDialogTitle
        title="Allocations"
        handleClose={handleClose}
        sx={{ position: 'sticky', top: 0 }}
      />
      <Box sx={styles.topBox}>
        <ReportsInputsData filters={filters} viewMode={viewMode} />
        <Box sx={styles.allocationsListBox}>
          {isLoading && <AppLoading />}
          {!isLoading &&
            projectsAllocations?.map((projectAllocation) => {
              return (
                <ProjectAllocationsCard
                  projectAllocations={projectAllocation}
                  key={projectAllocation.project.id}
                />
              );
            })}
        </Box>
      </Box>
    </Dialog>
  );
}
